import { offlineDb } from '../db/indexedDb';
import ApiService from './ApiService';
import { isConnected } from '../composables/useConnectivityStatus';

const MAX_RETRIES = 10;

const isNetworkError = (error) => {
  if (!error) return false;
  return !error.response || error.code === 'ERR_NETWORK' || !isConnected.value;
};

const deepCopy = (payload) => JSON.parse(JSON.stringify(payload));

class OfflineSubmissionService {
  async queueSubmission(endpoint, payload, submissionId = crypto.randomUUID()) {
    const now = new Date().toISOString();

    const entry = {
      id: crypto.randomUUID(),
      endpoint,
      payload: deepCopy(payload),
      created_at: now,
      retry_count: 0,
      synced: false,
      last_attempt_at: null,
      status: 'queued',
      submission_id: submissionId,
    };

    await offlineDb.submissions.add(entry);
    return entry;
  }

  async submitWithOfflineSupport(endpoint, payload) {
    const submissionId = crypto.randomUUID();
    const headers = { 'X-Submission-ID': submissionId };

    if (!isConnected.value) {
      const queued = await this.queueSubmission(endpoint, payload, submissionId);
      return { status: 'queued', queued };
    }

    try {
      const response = await ApiService.post(endpoint, payload, { headers });
      return { status: 'synced', response };
    } catch (error) {
      if (isNetworkError(error)) {
        const queued = await this.queueSubmission(endpoint, payload, submissionId);
        return { status: 'queued', queued };
      }
      throw error;
    }
  }

  async getPendingSubmissions() {
    return offlineDb.submissions
      .where('status')
      .anyOf(['queued', 'syncing', 'failed'])
      .sortBy('created_at');
  }

  async getPendingCount() {
    return offlineDb.submissions.where('status').anyOf(['queued', 'syncing', 'failed']).count();
  }

  async getFailedCount() {
    return offlineDb.submissions.where('status').equals('failed').count();
  }

  async removeSubmission(id) {
    return offlineDb.submissions.delete(id);
  }

  async markAttemptFailure(id, retryCount) {
    const now = new Date().toISOString();
    const status = retryCount >= MAX_RETRIES ? 'failed' : 'queued';

    await offlineDb.submissions.update(id, {
      retry_count: retryCount,
      last_attempt_at: now,
      status,
    });
  }

  async markSyncing(id) {
    await offlineDb.submissions.update(id, {
      status: 'syncing',
      last_attempt_at: new Date().toISOString(),
    });
  }

  async markQueued(id) {
    await offlineDb.submissions.update(id, {
      status: 'queued',
    });
  }

  async getById(id) {
    return offlineDb.submissions.get(id);
  }

  getMaxRetries() {
    return MAX_RETRIES;
  }
}

export default new OfflineSubmissionService();
